import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Camera, Upload, Scale, Plus, Ruler, Trash2, ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { logActivity } from '@/lib/activityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEASUREMENT_FIELDS = [
  { key: 'chest', label: 'Chest', icon: '📏' },
  { key: 'waist', label: 'Waist', icon: '📏' },
  { key: 'hips', label: 'Hips', icon: '📏' },
  { key: 'left_arm', label: 'Left Arm', icon: '💪' },
  { key: 'right_arm', label: 'Right Arm', icon: '💪' },
  { key: 'left_thigh', label: 'Left Thigh', icon: '🦵' },
  { key: 'right_thigh', label: 'Right Thigh', icon: '🦵' },
  { key: 'neck', label: 'Neck', icon: '📏' },
];

const PHOTO_TYPES = [
  { value: 'front', label: 'Front View' },
  { value: 'side', label: 'Side View' },
  { value: 'back', label: 'Back View' },
];

export default function ClientProgress({ user, onLogout }) {
  const [weightLogs, setWeightLogs] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({}); // Store blob URLs for authenticated photo access
  const [loading, setLoading] = useState(true);
  const [showMeasurementForm, setShowMeasurementForm] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [savingMeasurement, setSavingMeasurement] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [expandedMeasurement, setExpandedMeasurement] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [measurementForm, setMeasurementForm] = useState({
    date: new Date().toISOString().split('T')[0],
    chest: '', waist: '', hips: '', left_arm: '', right_arm: '',
    left_thigh: '', right_thigh: '', neck: '', notes: ''
  });
  const [photoForm, setPhotoForm] = useState({
    date: new Date().toISOString().split('T')[0],
    photo_type: 'front',
    notes: '',
    file: null
  });

  useEffect(() => {
    logActivity('progress_viewed');
    fetchProgressData();
  }, []);

  // Load photo blob URLs when photos change
  useEffect(() => {
    const loadPhotoUrls = async () => {
      const newUrls = {};
      for (const photo of photos) {
        try {
          const response = await axios.get(`${API}/progress-photos/file/${photo.filename}`, {
            responseType: 'blob'
          });
          newUrls[photo.id] = URL.createObjectURL(response.data);
        } catch (error) {
          console.error(`Failed to load photo ${photo.filename}:`, error);
        }
      }
      if (Object.keys(newUrls).length > 0) {
        setPhotoUrls(newUrls);
      }
    };
    
    if (photos.length > 0) {
      loadPhotoUrls();
    }
  }, [photos]);

  const fetchProgressData = async () => {
    try {
      const [weightRes, measurementsRes, photosRes] = await Promise.all([
        axios.get(`${API}/weight-logs/my-logs`).catch(() => ({ data: [] })),
        axios.get(`${API}/body-measurements`).catch(() => ({ data: [] })),
        axios.get(`${API}/progress-photos`).catch(() => ({ data: [] }))
      ]);
      setWeightLogs(weightRes.data || []);
      setMeasurements(measurementsRes.data || []);
      setPhotos(photosRes.data || []);
    } catch (error) {
      console.error('Failed to load progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openMeasurementForm = async () => {
    // Pre-fill form with latest measurements
    try {
      const response = await axios.get(`${API}/body-measurements/latest`);
      if (response.data) {
        setMeasurementForm({
          date: new Date().toISOString().split('T')[0],
          chest: response.data.chest || '',
          waist: response.data.waist || '',
          hips: response.data.hips || '',
          left_arm: response.data.left_arm || '',
          right_arm: response.data.right_arm || '',
          left_thigh: response.data.left_thigh || '',
          right_thigh: response.data.right_thigh || '',
          neck: response.data.neck || '',
          notes: ''
        });
      }
    } catch (error) {
      // No previous measurements, keep form empty
      console.log('No previous measurements found');
    }
    setShowMeasurementForm(true);
  };

  const handleSaveMeasurement = async () => {
    setSavingMeasurement(true);
    try {
      const data = {
        date: measurementForm.date,
        chest: measurementForm.chest ? parseFloat(measurementForm.chest) : null,
        waist: measurementForm.waist ? parseFloat(measurementForm.waist) : null,
        hips: measurementForm.hips ? parseFloat(measurementForm.hips) : null,
        left_arm: measurementForm.left_arm ? parseFloat(measurementForm.left_arm) : null,
        right_arm: measurementForm.right_arm ? parseFloat(measurementForm.right_arm) : null,
        left_thigh: measurementForm.left_thigh ? parseFloat(measurementForm.left_thigh) : null,
        right_thigh: measurementForm.right_thigh ? parseFloat(measurementForm.right_thigh) : null,
        neck: measurementForm.neck ? parseFloat(measurementForm.neck) : null,
        notes: measurementForm.notes || null
      };

      await axios.post(`${API}/body-measurements`, data);
      toast.success('Measurements saved!');
      setShowMeasurementForm(false);
      setMeasurementForm({
        date: new Date().toISOString().split('T')[0],
        chest: '', waist: '', hips: '', left_arm: '', right_arm: '',
        left_thigh: '', right_thigh: '', neck: '', notes: ''
      });
      fetchProgressData();
    } catch (error) {
      toast.error('Failed to save measurements');
    } finally {
      setSavingMeasurement(false);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoForm.file) {
      toast.error('Please select a photo');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photoForm.file);
      formData.append('date', photoForm.date);
      formData.append('photo_type', photoForm.photo_type);
      if (photoForm.notes) formData.append('notes', photoForm.notes);

      await axios.post(`${API}/progress-photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Photo uploaded!');
      setShowPhotoUpload(false);
      setPhotoForm({
        date: new Date().toISOString().split('T')[0],
        photo_type: 'front',
        notes: '',
        file: null
      });
      fetchProgressData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/progress-photos/${photoId}`);
      toast.success('Photo deleted');
      setSelectedPhoto(null);
      fetchProgressData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteMeasurement = async (date) => {
    if (!confirm('Delete this measurement?')) return;
    try {
      await axios.delete(`${API}/body-measurements/${date}`);
      toast.success('Measurement deleted');
      fetchProgressData();
    } catch (error) {
      toast.error('Failed to delete measurement');
    }
  };

  const getWeightChartData = () => {
    return weightLogs
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(log => ({
        date: new Date(log.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        weight: log.weight
      }));
  };

  const getMeasurementChartData = (field) => {
    return measurements
      .filter(m => m[field] != null)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(m => ({
        date: new Date(m.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        value: m[field]
      }));
  };

  const getLatestMeasurement = () => measurements.length > 0 ? measurements[0] : null;
  const getFirstMeasurement = () => measurements.length > 0 ? measurements[measurements.length - 1] : null;

  const calculateChange = (field) => {
    const latest = getLatestMeasurement();
    const first = getFirstMeasurement();
    if (!latest || !first || latest[field] == null || first[field] == null) return null;
    return (latest[field] - first[field]).toFixed(1);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  const groupPhotosByDate = () => {
    const grouped = {};
    photos.forEach(photo => {
      if (!grouped[photo.date]) grouped[photo.date] = [];
      grouped[photo.date].push(photo);
    });
    return Object.entries(grouped).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  const latestMeasurement = getLatestMeasurement();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Progress</h1>
          <p className="text-gray-500 mt-1">Track your transformation journey</p>
        </div>

        <Tabs defaultValue="weight" className="space-y-6">
          <TabsList className="bg-white border shadow-sm p-1 rounded-xl">
            <TabsTrigger value="weight" className="rounded-lg data-[state=active]:bg-blue-500 data-[state=active]:text-white px-4">
              <Scale className="h-4 w-4 mr-2" />Weight
            </TabsTrigger>
            <TabsTrigger value="measurements" className="rounded-lg data-[state=active]:bg-blue-500 data-[state=active]:text-white px-4">
              <Ruler className="h-4 w-4 mr-2" />Measurements
            </TabsTrigger>
            <TabsTrigger value="photos" className="rounded-lg data-[state=active]:bg-blue-500 data-[state=active]:text-white px-4">
              <Camera className="h-4 w-4 mr-2" />Photos
            </TabsTrigger>
          </TabsList>

          {/* Weight Tab */}
          <TabsContent value="weight" className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Weight Trend</CardTitle>
                <CardDescription>Your weight over time</CardDescription>
              </CardHeader>
              <CardContent>
                {weightLogs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Scale className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-lg font-medium mb-2">No weight data yet</p>
                    <p className="text-sm">Start logging your weight to see progress</p>
                  </div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getWeightChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                        <YAxis stroke="#9ca3af" fontSize={12} domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                        <Line type="monotone" dataKey="weight" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Measurements Tab */}
          <TabsContent value="measurements" className="space-y-6">
            {latestMeasurement && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {['waist', 'chest', 'hips', 'left_arm'].map(field => {
                  const change = calculateChange(field);
                  const fieldInfo = MEASUREMENT_FIELDS.find(f => f.key === field);
                  return (
                    <Card key={field} className="border-0 shadow-sm">
                      <CardContent className="p-4">
                        <p className="text-sm text-gray-500 mb-1">{fieldInfo?.label}</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {latestMeasurement[field] ? `${latestMeasurement[field]} cm` : '—'}
                        </p>
                        {change && (
                          <p className={`text-sm mt-1 ${parseFloat(change) < 0 ? 'text-green-600' : parseFloat(change) > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                            {parseFloat(change) > 0 ? '+' : ''}{change} cm
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end">
              <Button 
                className="bg-blue-500 hover:bg-blue-600"
                onClick={openMeasurementForm}
              >
                <Plus className="h-4 w-4 mr-2" />Add Measurement
              </Button>
              
              <Dialog open={showMeasurementForm} onOpenChange={setShowMeasurementForm}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Log Body Measurements</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={measurementForm.date} onChange={(e) => setMeasurementForm({ ...measurementForm, date: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {MEASUREMENT_FIELDS.map(field => (
                        <div key={field.key}>
                          <Label className="text-sm">{field.icon} {field.label} (cm)</Label>
                          <Input 
                            type="number" 
                            step="0.1" 
                            placeholder={measurementForm[field.key] ? '' : '0.0'}
                            value={measurementForm[field.key]} 
                            onChange={(e) => setMeasurementForm({ ...measurementForm, [field.key]: e.target.value })}
                            className={measurementForm[field.key] ? '' : 'text-gray-400'}
                          />
                        </div>
                      ))}
                    </div>
                    <div>
                      <Label>Notes (optional)</Label>
                      <Input placeholder="Any notes..." value={measurementForm.notes} onChange={(e) => setMeasurementForm({ ...measurementForm, notes: e.target.value })} />
                    </div>
                    <Button onClick={handleSaveMeasurement} disabled={savingMeasurement} className="w-full bg-blue-500 hover:bg-blue-600">
                      {savingMeasurement ? 'Saving...' : 'Save Measurements'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {measurements.length > 1 && getMeasurementChartData('waist').length > 1 && (
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle>Waist Trend</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getMeasurementChartData('waist')}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                        <YAxis stroke="#9ca3af" fontSize={12} domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                        <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', r: 4 }} name="Waist (cm)" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Measurement History</CardTitle>
                <CardDescription>{measurements.length} record{measurements.length !== 1 ? 's' : ''}</CardDescription>
              </CardHeader>
              <CardContent>
                {measurements.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Ruler className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-lg font-medium mb-2">No measurements yet</p>
                    <p className="text-sm">Add your first measurement to start tracking</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {measurements.map((m, index) => {
                      // Count how many measurements were recorded
                      const measurementCount = MEASUREMENT_FIELDS.filter(f => m[f.key]).length;
                      return (
                        <div key={m.id || index} className="border border-gray-100 rounded-xl overflow-hidden">
                          <button onClick={() => setExpandedMeasurement(expandedMeasurement === index ? null : index)} className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                            <div className="text-left">
                              <p className="font-medium text-gray-900">{formatDate(m.date)}</p>
                              <p className="text-sm text-gray-500">{measurementCount} measurement{measurementCount !== 1 ? 's' : ''} recorded</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteMeasurement(m.date); }} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              {expandedMeasurement === index ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                            </div>
                          </button>
                        {expandedMeasurement === index && (
                          <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              {MEASUREMENT_FIELDS.map(field => (
                                <div key={field.key} className="text-sm">
                                  <span className="text-gray-500">{field.label}:</span>{' '}
                                  <span className="font-medium">{m[field.key] ? `${m[field.key]} cm` : '—'}</span>
                                </div>
                              ))}
                            </div>
                            {m.notes && <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200">{m.notes}</p>}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-6">
            <div className="flex justify-end">
              <Dialog open={showPhotoUpload} onOpenChange={setShowPhotoUpload}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-500 hover:bg-blue-600">
                    <Upload className="h-4 w-4 mr-2" />Upload Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Upload Progress Photo</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={photoForm.date} onChange={(e) => setPhotoForm({ ...photoForm, date: e.target.value })} />
                    </div>
                    <div>
                      <Label>Photo Type</Label>
                      <Select value={photoForm.photo_type} onValueChange={(value) => setPhotoForm({ ...photoForm, photo_type: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PHOTO_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Photo</Label>
                      <Input type="file" accept="image/*" onChange={(e) => setPhotoForm({ ...photoForm, file: e.target.files[0] })} />
                      {photoForm.file && <p className="text-sm text-gray-500 mt-1">{photoForm.file.name}</p>}
                    </div>
                    <div>
                      <Label>Notes (optional)</Label>
                      <Input placeholder="Any notes..." value={photoForm.notes} onChange={(e) => setPhotoForm({ ...photoForm, notes: e.target.value })} />
                    </div>
                    <Button onClick={handlePhotoUpload} disabled={uploadingPhoto || !photoForm.file} className="w-full bg-blue-500 hover:bg-blue-600">
                      {uploadingPhoto ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : 'Upload Photo'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {photos.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12">
                  <div className="text-center text-gray-500">
                    <Camera className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-lg font-medium mb-2">No progress photos yet</p>
                    <p className="text-sm">Upload your first photo to track your visual transformation</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {groupPhotosByDate().map(([date, datePhotos]) => (
                  <Card key={date} className="border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{formatDate(date)}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-3">
                        {datePhotos.map(photo => (
                          <div key={photo.id} className="relative group cursor-pointer" onClick={() => setSelectedPhoto(photo)}>
                            {photoUrls[photo.id] ? (
                              <img src={photoUrls[photo.id]} alt={photo.photo_type} className="w-full aspect-square object-cover rounded-lg" />
                            ) : (
                              <div className="w-full aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <span className="text-white text-sm font-medium capitalize">{photo.photo_type}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Photo Viewer Modal */}
            {selectedPhoto && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
                <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
                  <button onClick={() => setSelectedPhoto(null)} className="absolute -top-12 right-0 text-white hover:text-gray-300">
                    <X className="h-8 w-8" />
                  </button>
                  {photoUrls[selectedPhoto.id] ? (
                    <img src={photoUrls[selectedPhoto.id]} alt={selectedPhoto.photo_type} className="w-full rounded-lg" />
                  ) : (
                    <div className="w-full h-96 bg-gray-800 rounded-lg flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                  )}
                  <div className="mt-4 flex justify-between items-center text-white">
                    <div>
                      <p className="font-medium capitalize">{selectedPhoto.photo_type} view</p>
                      <p className="text-sm text-gray-300">{formatDate(selectedPhoto.date)}</p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => handleDeletePhoto(selectedPhoto.id)}>
                      <Trash2 className="h-4 w-4 mr-2" />Delete
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
